import type { TextWithSidePanelComponent } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { isParagraphHtml } from "../../utils/paragraphHtml";
import { paragraphHtmlToSafeHtml } from "../../utils/inlineIcons";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/**
 * Bloc de texte encadré, avec un panneau latéral FACULTATIF portant une image et un
 * bouton.
 *
 * Sans panneau, la grille à deux colonnes disparaît et le texte prend toute la
 * largeur. Laisser la colonne vide donnerait un bloc arrêté aux deux tiers avec un
 * vide inexpliqué à sa droite, ce qui se lit comme une image qui n'a pas chargé.
 */
export function TextWithSidePanelRenderer({ component }: { component: TextWithSidePanelComponent }) {
  const { title, paragraphs, panelTitle, imageSrc, imageAlt, ctaLabel, ctaHref } = component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  // Un titre de panneau, une image ou un bouton suffit à justifier la colonne. Rien
  // des trois : il n'y a pas de panneau, pas seulement un panneau incomplet.
  const aUnPanneau = Boolean(panelTitle || src || ctaLabel);

  return (
    <section>
      <div className={aUnPanneau ? "grid gap-4 md:grid-cols-[2fr_1fr] md:items-start" : "grid gap-4"}>
        <div className="rounded-sm bg-[#F3F7FC] p-5 md:p-7 xl:p-8">
          <h3
            className="text-xl font-bold leading-tight text-[#005CA9] md:text-[1.35rem] xl:text-2xl"
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h3>
          {/* Interligne mobile resserré (1,5) comme partout ailleurs sous 768px —
              1,75 délayait le paragraphe ; le format aéré revient dès la tablette. */}
          <div className="mt-4 space-y-4 text-base xl:text-lg leading-6 md:leading-7 text-[#0F2849] rich-paragraph-block">
            {(paragraphs ?? []).map((paragraph, index) =>
              isParagraphHtml(paragraph) ? (
                <p
                  key={index}
                  className="rich-paragraph-block"
                  dangerouslySetInnerHTML={{ __html: paragraphHtmlToSafeHtml(paragraph) }}
                />
              ) : (
                <p key={index}>{paragraph}</p>
              )
            )}
          </div>
        </div>
        {aUnPanneau ? (
          <aside className="flex flex-col gap-4 bg-transparent p-4 pt-0 md:p-5 md:pt-0">
            {panelTitle ? (
              <h4
                className="text-xl font-bold leading-tight text-[#005CA9] md:text-[1.35rem] xl:text-2xl"
                style={{ fontFamily: "var(--font-aleo)" }}
              >
                {panelTitle}
              </h4>
            ) : null}
            {src ? (
              <RuntimeImage
                src={src}
                alt={imageAlt ?? ""}
                className={mergeClassNames("w-full object-cover aspect-[4/3]", overrideProps.className)}
                style={mergeStyles(objectPositionStyle, overrideProps.style)}
                priority
              />
            ) : null}
            {ctaLabel ? (
              <GuardedLink
                href={ctaHref}
                label={ctaLabel}
                className="inline-flex self-start items-center justify-center rounded-full bg-[#FFA401] px-4 py-2 text-sm xl:text-base font-semibold text-white transition hover:opacity-90"
              >
                {ctaLabel}
              </GuardedLink>
            ) : null}
          </aside>
        ) : null}
      </div>
    </section>
  );
}
